import { useEffect, useState } from "react";
import { Plus, Trash2, FileText, Pencil, Copy, ShieldCheck, Wand2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, StatusPill, NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VEHICLES, VehicleBadge } from "@/components/VehicleBadge";
import { AccessToggle } from "@/components/AccessToggle";
import { LoginActivity } from "@/components/LoginActivity";

const ROLE_DESC = { admin: "Full access", production: "Orders, production & raw materials", delivery: "Delivery app only", qa: "QA bug tracker only", sales: "Leads assigned to them only", accountant: "GST bills & returns only" };

function PhoneEdit({ u }) {
  const [phone, setPhone] = useState(u.phone || "");
  const save = async () => {
    try { await api.put(`/users/${u.id}/phone`, { phone }); toast.success("WhatsApp number saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-3 flex gap-2">
      <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={u.role === "sales" ? "WhatsApp number (for follow-up list)" : "WhatsApp number (for load list)"} className="h-9 text-sm" data-testid={`user-phone-edit-${u.id}`} />
      <Button size="sm" variant="outline" onClick={save} disabled={phone === (u.phone || "")} data-testid={`user-phone-save-${u.id}`}>Save</Button>
    </div>
  );
}

const genPassword = () => {
  const a = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ", n = "23456789";
  const pick = (s) => s[crypto.getRandomValues(new Uint32Array(1))[0] % s.length];
  return Array.from({ length: 7 }, () => pick(a)).join("") + pick(n) + pick(n) + pick(n);
};

function PasswordStatus({ u }) {
  if (!u.must_change_password) return <div className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700" data-testid={`user-pw-status-${u.id}`}><ShieldCheck className="h-3.5 w-3.5" />{u.password_changed_at ? `Own password set on ${u.password_changed_at.slice(0, 10)}` : "Password set"}</div>;
  if (!u.temp_password) return <div className="mt-2 rounded-lg bg-[#FAF4E8] px-3 py-2 text-xs text-[#9A4A2A]" data-testid={`user-pw-status-${u.id}`}>Must set a new password at next login</div>;
  const copy = () => { navigator.clipboard?.writeText(u.temp_password); toast.success("Temporary password copied"); };
  return (
    <div className="mt-2 flex items-center gap-2 rounded-lg bg-[#FAF4E8] px-3 py-2 text-xs" data-testid={`user-pw-status-${u.id}`}>
      <span className="text-muted-foreground">Temporary password:</span>
      <code className="font-semibold text-[#0F382C]" data-testid={`user-temp-password-${u.id}`}>{u.temp_password}</code>
      <button type="button" onClick={copy} aria-label="Copy" className="text-[#D96B43]" data-testid={`user-temp-copy-${u.id}`}><Copy className="h-3.5 w-3.5" /></button>
    </div>
  );
}

const HANDOUT_ROLES = ["delivery", "sales", "production", "accountant", "qa"];

function CapacityEdit({ u }) {
  const [cap, setCap] = useState(u.capacity ?? "");
  const save = async () => {
    try { await api.put(`/users/${u.id}/capacity`, { capacity: cap === "" ? null : +cap }); toast.success("Max drops saved"); u.capacity = cap === "" ? null : +cap; setCap(cap); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-2 flex items-center gap-2">
      <Input type="number" min="1" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="Max drops (blank = default)" className="h-9 text-sm" data-testid={`user-capacity-edit-${u.id}`} />
      <Button size="sm" variant="outline" onClick={save} disabled={String(cap) === String(u.capacity ?? "")} data-testid={`user-capacity-save-${u.id}`}>Save</Button>
    </div>
  );
}

function VehicleEdit({ u, onSaved }) {
  const save = async (e) => {
    const vehicle = e.target.value || null;
    try { await api.put(`/users/${u.id}/vehicle`, { vehicle }); toast.success("Vehicle saved"); onSaved(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <NativeSelect className="mt-2 h-9 text-sm" value={u.vehicle || ""} onChange={save} data-testid={`user-vehicle-select-${u.id}`}>
      <option value="">Vehicle not set</option>
      {Object.entries(VEHICLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
    </NativeSelect>
  );
}

const EMPTY = { name: "", email: "", password: "", role: "delivery", phone: "" };

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [askAll, setAskAll] = useState(false);
  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const openAdd = () => { setEditId(null); setF(EMPTY); setOpen(true); };
  const openEdit = (u) => { setEditId(u.id); setF({ name: u.name, email: u.email, password: "", role: u.role, phone: u.phone || "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editId) await api.put(`/users/${editId}`, f); else await api.post("/users", f);
      toast.success(editId ? "Team member updated" : "Team member added"); setOpen(false); setF(EMPTY); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const forceAll = async () => {
    try { const r = await api.post("/users/require-password-change"); toast.success(`${r.data.count} staff will set a new password at next login`); setAskAll(false); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (u) => { try { await api.delete(`/users/${u.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="team-page">
      <PageHeader eyebrow="Team" title="Staff & roles" subtitle="Give your kitchen staff and delivery boys their own login.">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => setAskAll(true)} data-testid="force-all-pw-btn"><KeyRound className="mr-1 h-4 w-4" />Ask all staff to set new passwords</Button>
          <Button className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" onClick={openAdd} data-testid="add-user-btn"><Plus className="mr-1 h-4 w-4" />Add member</Button>
        </div>
      </PageHeader>
      <Dialog open={askAll} onOpenChange={setAskAll}>
        <DialogContent className="max-w-sm" data-testid="force-all-pw-dialog">
          <DialogHeader><DialogTitle className="font-display">Ask all staff to set new passwords?</DialogTitle>
            <DialogDescription>Everyone except you signs in once more with their current password and must then choose a new one. Nobody is logged out of their data.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setAskAll(false)} data-testid="force-all-pw-cancel">Cancel</Button>
            <Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={forceAll} data-testid="force-all-pw-confirm">Yes, ask everyone</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {users.map((u) => (
          <div key={u.id} className={`rounded-2xl border border-[#E7E2D6] bg-white p-5 transition-opacity ${u.active === false ? "opacity-60" : ""}`} data-testid={`user-card-${u.id}`}>
            <div className="flex items-start justify-between">
              <div><div className="flex items-center gap-2 font-display text-lg font-semibold">{u.name}<VehicleBadge vehicle={u.vehicle} testid={`user-vehicle-badge-${u.id}`} /></div><div className="text-sm text-muted-foreground">{u.email}</div></div>
              <div className="flex">
                <Button variant="ghost" size="icon" onClick={() => openEdit(u)} aria-label="Edit" data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                {u.id !== user.id && <Button variant="ghost" size="icon" onClick={() => remove(u)} aria-label="Delete" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
            <PasswordStatus u={u} />
            {u.id !== user.id && <AccessToggle u={u} onChanged={load} />}
            <div className="mt-3 flex items-center gap-2"><StatusPill status={u.role === "admin" ? "confirmed" : u.role === "production" ? "pending" : "skipped"} label={u.role} /><span className="text-xs text-muted-foreground">{ROLE_DESC[u.role]}</span></div>
            {HANDOUT_ROLES.includes(u.role) && <a href={`/guides/${u.role}.pdf`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#D96B43] hover:underline" data-testid={`user-handout-${u.id}`}><FileText className="h-3.5 w-3.5" />One-page role handout (PDF)</a>}
            {u.role === "sales" && <PhoneEdit u={u} />}
            {u.role === "delivery" && <><PhoneEdit u={u} /><CapacityEdit u={u} /><VehicleEdit u={u} onSaved={load} /></>}
          </div>
        ))}
      </div>
      <LoginActivity />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit team member" : "Add team member"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name", "Name"], ["email", "Email"], ["password", editId ? "New temporary password (leave empty to keep current)" : "Temporary password"], ["phone", "Phone"]].map(([k, l]) => (
              <div key={k} className="space-y-1"><Label>{l}</Label>{k === "password"
                ? <><div className="flex gap-2"><div className="flex-1"><PasswordInput value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} autoComplete="new-password" data-testid="user-password-input" /></div>
                    <Button type="button" variant="outline" onClick={() => setF({ ...f, password: genPassword() })} data-testid="user-password-generate-btn"><Wand2 className="mr-1 h-4 w-4" />Generate</Button></div>
                  <p className="text-xs text-muted-foreground">{editId === user.id ? "This becomes your new password." : "They will be asked to set their own password at first login. You can see it on their card until then."}</p></>
                : <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`user-${k}-input`} />}</div>
            ))}
            <div className="space-y-1"><Label>Role</Label>
              <NativeSelect value={f.role} disabled={editId === user.id} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="user-role-select">
                <option value="delivery">Delivery boy</option><option value="sales">Salesperson</option><option value="accountant">Accountant</option><option value="production">Production staff</option><option value="qa">QA tester</option><option value="admin">Admin</option>
              </NativeSelect>
            </div>
          </div>
          <DialogFooter><Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={save} data-testid="user-save-btn">{editId ? "Save changes" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
